// ESLint's TypeScript plugin cannot resolve the @/ path alias when tsconfig.json
// has a Windows path-casing mismatch. TypeScript itself compiles cleanly (tsc --noEmit
// passes with zero errors). The three rules below fire as false positives because
// PlaybackPositionContext is fully typed but appears as `any` to the ESLint parser.
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import arrowIcon from '@/assets/icons/arrow.svg';
import fullScreenIcon from '@/assets/icons/full-screen.svg';
import newClipIcon from '@/assets/icons/new-clip.svg';
// import pencilIcon from '@/assets/icons/pencil.svg';
import searchIcon from '@/assets/icons/search.svg';
import { TranscriptItem } from '@/components/Transcript/Transcript';
import { PlaybackPositionContext } from '@/contexts/WorkbenchContext';
import { Phrase, usePhrases } from '@/hooks/usePhrases';
import { TranscriptItem as TranscriptItemModel } from '@/types/transcript';
import { parseTimeToSeconds, toHHMMSS } from '@/utils';
import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VariableSizeList } from 'react-window';
import { useDebouncedCallback } from 'use-debounce';
import styles from './WorkbenchTranscript.module.scss';

// ---------------------------------------------------------------------------
// PhraseRow — memoized row renderer used by VariableSizeList.
// Only re-renders when props that affect THIS row's visual output change.
// ---------------------------------------------------------------------------

// Number of phrases (sentences) grouped into each virtual row.
// Phrases within a chunk render as display:inline so they flow as one continuous
// paragraph — identical visual to the original non-virtualized layout.
// With 25 phrases/chunk a 10h transcript (~5000 phrases) becomes ~200 virtual rows,
// keeping only ~125 visible phrases (~1250 word spans) in the DOM at any time.
const PHRASES_PER_CHUNK = 25;

// Conservative starting estimate for one chunk row (25 phrases).
// Actual heights are measured via useLayoutEffect and cached per-row.
// A larger estimate means fewer overlapping rows during initial paint.
const ESTIMATED_ROW_HEIGHT = 220;

/** Avoid 0×0 first paint in popouts; react-window needs numeric width ASAP. */
function getInitialListDimensions(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  return { width: window.innerWidth, height: window.innerHeight };
}

interface PhraseRowData {
  /** Paragraph chunks — each chunk is PHRASES_PER_CHUNK consecutive phrases */
  paragraphChunks: Phrase[][];
  edits: Record<number, string>;
  selectedStartIndex: number | null;
  selectedEndIndex: number | null;
  /** Index of the chunk that contains the currently active (playing) word */
  currentChunkIndex: number;
  /** Start time of the currently active word — only relevant when row === currentChunkIndex */
  activeWordStart: number | null;
  /** Start time of the word to pulse-highlight after a seek navigation (or null) */
  seekHighlightStart: number | null;
  searchQuery: string;
  searchMatchIndices: number[];
  searchMatchCount: number;
  currentMatchIndex: number;
  isEditMode: boolean;
  handleStartHandleMouseDown(e: React.MouseEvent): void;
  handleEndHandleMouseDown(e: React.MouseEvent): void;
  handleWordUpdate(index: number, text: string): void;
  selectItem(item: TranscriptItemModel): void;
  setRowHeight(index: number, height: number): void;
}

interface PhraseRowProps {
  index: number;
  style: React.CSSProperties;
  data: PhraseRowData;
}

const PhraseRowComponent = ({ index, style, data }: PhraseRowProps): ReactElement | null => {
  const {
    paragraphChunks,
    edits,
    selectedStartIndex,
    selectedEndIndex,
    currentChunkIndex,
    activeWordStart,
    seekHighlightStart,
    searchQuery,
    searchMatchIndices,
    searchMatchCount,
    currentMatchIndex,
    isEditMode,
    handleStartHandleMouseDown,
    handleEndHandleMouseDown,
    handleWordUpdate,
    selectItem,
    setRowHeight,
  } = data;

  const chunk = paragraphChunks[index];
  // Ref to the inner content div whose height we report to react-window.
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Use ResizeObserver (async, fires after browser layout) instead of
  // useLayoutEffect (sync, fires during React's commit phase).
  //
  // Why ResizeObserver fixes the "going away / coming back" flicker:
  //   useLayoutEffect fires synchronously on EVERY render. It calls
  //   setRowHeight → resetAfterIndex → react-window forceUpdates → rows
  //   re-render → useLayoutEffect fires again → cascade. react-window
  //   unmounts rows it deems "off-screen" mid-cascade → visible flicker.
  //
  //   ResizeObserver fires asynchronously ONLY when the element's size
  //   actually changes. No synchronous loop is possible. Heights are
  //   corrected after the browser has already painted the first frame,
  //   so there is no flicker caused by the measurement itself.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h) setRowHeight(index, Math.ceil(h) + 6);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [index, setRowHeight]);

  if (!chunk) return null;

  const isCurrentChunk = index === currentChunkIndex;

  return (
    // Outer div keeps react-window's absolute positioning (top/left/width/height).
    // overflow:visible lets content extend beyond the estimated height while
    // ResizeObserver asynchronously corrects it via resetAfterIndex.
    <div style={{ ...style, overflow: 'hidden' }}>
      {/* Inner content div: padding provides the left/right transcript margin;
          ResizeObserver on this element reports actual rendered height. */}
      <div ref={contentRef} style={{ paddingLeft: 16, paddingRight: 16, boxSizing: 'border-box' }}>
        {/*
        All phrases in the chunk are display:inline spans, exactly as in the
        original non-virtualized layout — they flow as one continuous paragraph
        of text with no visual breaks between sentences.
      */}
        {chunk.map((phrase) => (
          <span key={phrase.startIndex} className={styles.phraseRow}>
            {phrase.words.map((item, wordIdx) => {
              const globalIndex = phrase.startIndex + wordIdx;
              const displayText = edits[globalIndex] ?? item.text;
              const displayItem = { ...item, text: displayText };
              const isSelected =
                selectedStartIndex !== null &&
                selectedEndIndex !== null &&
                globalIndex >= selectedStartIndex &&
                globalIndex <= selectedEndIndex;
              const isCurrent = isCurrentChunk && activeWordStart === item.start;
              const isStartHandle = selectedStartIndex === globalIndex;
              const isEndHandle = selectedEndIndex === globalIndex;
              const isFirstSelected = isSelected && globalIndex === selectedStartIndex;
              const isLastSelected = isSelected && globalIndex === selectedEndIndex;
              const isSearchMatch = !!(
                searchQuery.trim() && searchMatchIndices.includes(globalIndex)
              );
              const isCurrentSearchMatch =
                searchMatchCount > 0 && searchMatchIndices[currentMatchIndex] === globalIndex;

              const isSeekHighlight = seekHighlightStart === item.start;
              const wrapperClass = [
                styles.wordWrapper,
                isSelected ? styles.selectedWord : '',
                isFirstSelected ? styles.selectedWordFirst : '',
                isLastSelected ? styles.selectedWordLast : '',
                isSearchMatch ? styles.searchMatch : '',
                isCurrentSearchMatch ? styles.searchMatchCurrent : '',
                isSeekHighlight ? styles.seekHighlight : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <span
                  key={item.start}
                  className={wrapperClass}
                  data-start-time={item.start}
                  data-end-time={item.end}
                  data-word-index={globalIndex}
                >
                  {isStartHandle && (
                    <span
                      className={`${styles.selectionHandle} ${styles.startHandle}`}
                      onMouseDown={handleStartHandleMouseDown}
                      title="Drag to adjust start time"
                    >
                      <span className={styles.handleGrip} />
                    </span>
                  )}
                  <TranscriptItem
                    item={displayItem}
                    selected={isCurrent}
                    editable={isEditMode}
                    onSelect={selectItem}
                    onUpdate={
                      isEditMode
                        ? (newText: string) => handleWordUpdate(globalIndex, newText)
                        : undefined
                    }
                    className={isSelected ? styles.selectedWordText : undefined}
                  />
                  {isEndHandle && (
                    <span
                      className={`${styles.selectionHandle} ${styles.endHandle}`}
                      onMouseDown={handleEndHandleMouseDown}
                      title="Drag to adjust end time"
                    >
                      <span className={styles.handleGrip} />
                    </span>
                  )}
                  {wordIdx < phrase.words.length - 1 && '\u00A0'}
                </span>
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Custom comparator for React.memo — prevents re-renders of rows that are
 * not affected by the changed prop.  The most important case: when
 * `activeWordStart` ticks during video playback, only the one or two rows
 * whose "isCurrentPhrase" status changes actually re-render.
 */
const PhraseRow = React.memo(PhraseRowComponent, (prev, next) => {
  if (prev.index !== next.index) return false;
  if (prev.style !== next.style) return false;
  if (prev.data.paragraphChunks !== next.data.paragraphChunks) return false;
  if (prev.data.edits !== next.data.edits) return false;
  if (prev.data.selectedStartIndex !== next.data.selectedStartIndex) return false;
  if (prev.data.selectedEndIndex !== next.data.selectedEndIndex) return false;
  if (prev.data.isEditMode !== next.data.isEditMode) return false;
  if (prev.data.seekHighlightStart !== next.data.seekHighlightStart) return false;
  if (prev.data.searchMatchIndices !== next.data.searchMatchIndices) return false;
  if (prev.data.searchMatchCount !== next.data.searchMatchCount) return false;
  if (prev.data.currentMatchIndex !== next.data.currentMatchIndex) return false;
  if (prev.data.handleStartHandleMouseDown !== next.data.handleStartHandleMouseDown) return false;
  if (prev.data.handleEndHandleMouseDown !== next.data.handleEndHandleMouseDown) return false;
  if (prev.data.handleWordUpdate !== next.data.handleWordUpdate) return false;
  if (prev.data.selectItem !== next.data.selectItem) return false;
  if (prev.data.setRowHeight !== next.data.setRowHeight) return false;

  // Only re-render when this chunk's "active" status transitions.
  // During video playback, only the 1–2 chunks whose active status changes re-render.
  const prevIsActive = prev.data.currentChunkIndex === prev.index;
  const nextIsActive = next.data.currentChunkIndex === next.index;
  if (prevIsActive !== nextIsActive) return false;
  if (nextIsActive && prev.data.activeWordStart !== next.data.activeWordStart) return false;

  return true; // no relevant change — skip re-render
});

// ---------------------------------------------------------------------------
// Shared helper — finds the transcript word at (or closest to) a given time.
// Uses binary search for O(log n) on large transcripts.
// ---------------------------------------------------------------------------

function findWordAtTime(items: TranscriptItemModel[], timeSec: number): TranscriptItemModel | null {
  if (!items.length) return null;

  // Binary search: find the last word whose start <= timeSec
  let low = 0;
  let high = items.length - 1;
  while (low <= high) {
    const mid = (low + high) >>> 1;
    if (items[mid].start <= timeSec) low = mid + 1;
    else high = mid - 1;
  }
  const idx = Math.max(0, Math.min(high, items.length - 1));

  // Exact range match: word owns [start, nextStart) or [start, end) for last word
  const candidate = items[idx];
  const nextStart = items[idx + 1]?.start;
  const candidateEnd = nextStart !== undefined ? nextStart : candidate.end;
  if (timeSec >= candidate.start && timeSec < candidateEnd) return candidate;

  // Fallback: closest neighbour by absolute distance
  const prev = idx > 0 ? items[idx - 1] : null;
  const next = idx < items.length - 1 ? items[idx + 1] : null;
  let best = candidate;
  let bestDist = Math.abs(timeSec - candidate.start);
  if (prev && Math.abs(timeSec - prev.start) < bestDist) {
    best = prev;
    bestDist = Math.abs(timeSec - prev.start);
  }
  if (next && Math.abs(timeSec - next.start) < bestDist) {
    best = next;
  }
  return best;
}

// ---------------------------------------------------------------------------
// WorkbenchTranscript
// ---------------------------------------------------------------------------

const WorkbenchTranscript = ({
  transcriptItems,
  onCreateSegmentFromSelection,
  createSelectionButtonLabel = 'New Segment',
  onPopout,
  disableVirtualization = false,
  seekTrigger,
}: {
  transcriptItems: TranscriptItemModel[];
  onCreateSegmentFromSelection?(startTime: number, endTime: number): void | Promise<void>;
  createSelectionButtonLabel?: string;
  onPopout?(): void;
  disableVirtualization?: boolean;
  /** Incremented counter + target time.  Set by the parent on every segment/clip/topic
   *  time click so the transcript can scroll + highlight without relying on the
   *  currentPosition source field (which can be overwritten by video timeupdate). */
  seekTrigger?: { id: number; timeSec: number };
}): ReactElement => {
  const { setCurrentPosition, currentPosition } = useContext(PlaybackPositionContext);

  // Ref to the VariableSizeList's outer scroll element (used for querySelectorAll in drag/select)
  const listOuterRef = useRef<HTMLDivElement | null>(null);
  // Ref to the container div whose dimensions we pass to VariableSizeList
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [selectedRange, setSelectedRange] = useState<{ start: number; end: number } | null>(null);
  const [createSegmentError, setCreateSegmentError] = useState<string | null>(null);
  const [isCreatingSegment, setIsCreatingSegment] = useState(false);
  const [selectedStartIndex, setSelectedStartIndex] = useState<number | null>(null);
  const [selectedEndIndex, setSelectedEndIndex] = useState<number | null>(null);
  const [isDraggingHandle, setIsDraggingHandle] = useState<'start' | 'end' | null>(null);
  const dragStartRef = useRef<{ startX: number; startTime: number } | null>(null);
  const dragCurrentIndexRef = useRef<number | null>(null);

  // Search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Seek highlight: start time of the word to pulse after a seek navigation
  const [seekHighlightStart, setSeekHighlightStart] = useState<number | null>(null);

  // Edit mode: local overrides for word text (index -> new text)
  const [isEditMode, setIsEditMode] = useState(false);
  console.log('🚀 ~ WorkbenchTranscript ~ isEditMode:', setIsEditMode);
  const [edits, setEdits] = useState<Record<number, string>>({});

  const { phrases } = usePhrases(transcriptItems);

  // Group phrases into paragraph chunks so each virtual row contains PHRASES_PER_CHUNK
  // consecutive phrases rendered as display:inline — preserving the original continuous
  // text flow appearance within each chunk.
  const paragraphChunks = useMemo<Phrase[][]>(() => {
    const chunks: Phrase[][] = [];
    for (let i = 0; i < phrases.length; i += PHRASES_PER_CHUNK) {
      chunks.push(phrases.slice(i, i + PHRASES_PER_CHUNK));
    }
    return chunks;
  }, [phrases]);

  // VariableSizeList refs and height cache
  const listRef = useRef<VariableSizeList>(null);
  const rowHeightsRef = useRef<Record<number, number>>({});

  const setRowHeight = useCallback((index: number, height: number): void => {
    if (rowHeightsRef.current[index] !== height) {
      rowHeightsRef.current[index] = height;
      listRef.current?.resetAfterIndex(index, false);
    }
  }, []);

  // When browser zoom changes, react-window can keep stale measured heights.
  // Clear cached row sizes and force a full re-measure to prevent text overlap.
  const remeasureAllRows = useCallback((): void => {
    rowHeightsRef.current = {};
    listRef.current?.resetAfterIndex(0, true);
  }, []);

  const getItemSize = useCallback((index: number): number => {
    return rowHeightsRef.current[index] ?? ESTIMATED_ROW_HEIGHT;
  }, []);

  // Container dimensions for VariableSizeList (requires pixel values).
  // Seed from the viewport so the list never mounts at 0×0 (popup layout race).
  const [listDimensions, setListDimensions] = useState(getInitialListDimensions);

  // Always pass a positive numeric width to VariableSizeList (never '100%' — breaks layout in popouts).
  const effectiveListWidth = useMemo(() => {
    if (listDimensions.width > 0) return listDimensions.width;
    if (typeof window !== 'undefined') return Math.max(window.innerWidth, 1);
    return 320;
  }, [listDimensions.width]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId = 0;

    const MAX_HEIGHT_RETRIES = 20;

    // Popout / flex layouts may report 0 width or height on first frames; retry without
    // losing later non-zero size. Require real horizontal space — narrow first paint is
    // common in production popups.
    const updateDimensionsWithRetry = (attempt: number): void => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h === 0) {
        if (attempt < MAX_HEIGHT_RETRIES) {
          rafId = window.requestAnimationFrame(() => updateDimensionsWithRetry(attempt + 1));
        }
        return;
      }
      setListDimensions({ width: w, height: h });
    };

    const update = (): void => {
      updateDimensionsWithRetry(0);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);

    // Popout windows often settle width after first paint; re-measure once layout is stable.
    const safetyTimeoutId = window.setTimeout(() => updateDimensionsWithRetry(0), 500);

    return () => {
      observer.disconnect();
      window.clearTimeout(safetyTimeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  // When the container width changes, all row heights may change (text re-wraps).
  // Reset the height cache so rows are re-measured.
  const prevWidthRef = useRef(0);
  useEffect(() => {
    if (listDimensions.width > 0 && listDimensions.width !== prevWidthRef.current) {
      if (prevWidthRef.current !== 0) {
        remeasureAllRows();
      }
      prevWidthRef.current = listDimensions.width;
    }
  }, [listDimensions.width, remeasureAllRows]);

  // Re-measure rows when viewport/zoom changes (important in popout window).
  useEffect(() => {
    const handleViewportChange = (): void => {
      remeasureAllRows();
    };

    window.addEventListener('resize', handleViewportChange);
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    // In popout windows fonts/styles can settle asynchronously; remeasure once more.
    const timeoutId = window.setTimeout(handleViewportChange, 200);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.clearTimeout(timeoutId);
    };
  }, [remeasureAllRows]);

  // Derive current phrase index and active word start from context position.
  const currentTranscriptionItem = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- currentPosition can be undefined from context
    if (!transcriptItems.length || !currentPosition) return null;
    return findWordAtTime(transcriptItems, currentPosition.timeSec);
  }, [transcriptItems, currentPosition]);

  const currentChunkIndex = useMemo(() => {
    if (!currentTranscriptionItem) return -1;
    const wordIndex = transcriptItems.findIndex((w) => w.start === currentTranscriptionItem.start);
    if (wordIndex === -1) return -1;
    const phraseIndex = phrases.findIndex(
      (p) => wordIndex >= p.startIndex && wordIndex <= p.endIndex,
    );
    if (phraseIndex === -1) return -1;
    return Math.floor(phraseIndex / PHRASES_PER_CHUNK);
  }, [currentTranscriptionItem, transcriptItems, phrases]);

  const activeWordStart = currentTranscriptionItem?.start ?? null;

  const selectItem = useCallback(
    (item: TranscriptItemModel) => {
      setCurrentPosition(item.start, 'transcript');
    },
    [setCurrentPosition],
  );

  // Search: indices of words that match the query (using displayed text including edits)
  const searchMatchIndices = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    const indices: number[] = [];
    transcriptItems.forEach((item, index) => {
      const displayText = (edits[index] ?? item.text).toLowerCase();
      if (displayText.includes(q)) indices.push(index);
    });
    return indices;
  }, [transcriptItems, searchQuery, edits]);

  const searchMatchCount = searchMatchIndices.length;
  const canGoNext = searchMatchCount > 0 && currentMatchIndex < searchMatchCount - 1;
  const canGoPrev = searchMatchCount > 0 && currentMatchIndex > 0;

  const goToNextMatch = useCallback(() => {
    if (!canGoNext) return;
    setCurrentMatchIndex((i) => i + 1);
  }, [canGoNext]);

  const goToPrevMatch = useCallback(() => {
    if (!canGoPrev) return;
    setCurrentMatchIndex((i) => i - 1);
  }, [canGoPrev]);

  // When current search match changes, scroll the list to show the chunk containing the match
  useEffect(() => {
    if (searchMatchCount === 0) return;
    const safeIndex = Math.min(currentMatchIndex, searchMatchCount - 1);
    const wordIndex = searchMatchIndices[safeIndex];
    if (wordIndex === undefined) return;

    if (disableVirtualization) {
      const container = listOuterRef.current;
      if (!container) return;
      const target = container.querySelector<HTMLElement>(`[data-word-index="${wordIndex}"]`);
      target?.scrollIntoView({ block: 'center', inline: 'nearest' });
      return;
    }

    const phraseIndex = phrases.findIndex(
      (p) => wordIndex >= p.startIndex && wordIndex <= p.endIndex,
    );
    if (phraseIndex === -1) return;

    const chunkIndex = Math.floor(phraseIndex / PHRASES_PER_CHUNK);
    listRef.current?.scrollToItem(chunkIndex, 'center');
  }, [searchMatchIndices, searchMatchCount, currentMatchIndex, phrases, disableVirtualization]);

  // Reset current match index when query changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery]);

  const debouncedHandleSelectionChange = useDebouncedCallback(() => {
    // Skip while dragging a handle — the drag handler owns the indices;
    // the browser's native selection is stale and would overwrite them.
    if (isDraggingHandle) return;

    const selection = window.getSelection();
    // When selection is empty, do NOT clear selectedRange - e.g. scroll causes DOM unmount
    // and the browser clears selection; we keep the bar visible until user clicks Cancel
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }

    const range = selection.getRangeAt(0);
    const container = listOuterRef.current;
    if (!container) return;

    const text = selection.toString().trim();
    if (!text) {
      setSelectedRange(null);
      return;
    }

    const findTranscriptElement = (node: Node | null): HTMLElement | null => {
      if (!node) return null;
      let element: Element | null =
        node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
      while (element && element !== container) {
        if (element.hasAttribute('data-word-index')) return element as HTMLElement;
        // Selection may start in line number; check siblings
        const sibling = element.nextElementSibling || element.previousElementSibling;
        if (sibling?.hasAttribute('data-word-index')) return sibling as HTMLElement;
        element = element.parentElement;
      }
      return null;
    };

    const firstElement = findTranscriptElement(range.startContainer);
    const lastElement = findTranscriptElement(range.endContainer);

    if (
      !firstElement ||
      !lastElement ||
      !container.contains(firstElement) ||
      !container.contains(lastElement)
    ) {
      setSelectedRange(null);
      return;
    }

    const startAttr = firstElement.getAttribute('data-word-index');
    const endAttr = lastElement.getAttribute('data-word-index');
    if (startAttr === null || endAttr === null) {
      setSelectedRange(null);
      return;
    }
    const rawStartIndex = Number(startAttr);
    const rawEndIndex = Number(endAttr);
    if (
      Number.isNaN(rawStartIndex) ||
      Number.isNaN(rawEndIndex) ||
      rawStartIndex < 0 ||
      rawEndIndex < 0 ||
      rawStartIndex >= transcriptItems.length ||
      rawEndIndex >= transcriptItems.length
    ) {
      setSelectedRange(null);
      setSelectedStartIndex(null);
      setSelectedEndIndex(null);
      return;
    }

    const startIndex = Math.min(rawStartIndex, rawEndIndex);
    const endIndex = Math.max(rawStartIndex, rawEndIndex);
    const firstItem = transcriptItems[startIndex];
    const lastItem = transcriptItems[endIndex];
    if (!firstItem || !lastItem) return;

    setSelectedRange({
      start: firstItem.start,
      end: lastItem.end || lastItem.start + 5,
    });
    setSelectedStartIndex(startIndex);
    setSelectedEndIndex(endIndex);
  }, 100);

  /**
   * Handle creating a segment from the selected text.
   * Awaits the parent callback and only clears the selection on success.
   */
  const handleCreateSegment = useCallback(async () => {
    if (!selectedRange || !onCreateSegmentFromSelection) return;
    setCreateSegmentError(null);
    setIsCreatingSegment(true);
    try {
      await Promise.resolve(onCreateSegmentFromSelection(selectedRange.start, selectedRange.end));
      setSelectedRange(null);
      setSelectedStartIndex(null);
      setSelectedEndIndex(null);
      window.getSelection()?.removeAllRanges();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create segment. Please try again.';
      setCreateSegmentError(message);
    } finally {
      setIsCreatingSegment(false);
    }
  }, [selectedRange, onCreateSegmentFromSelection]);

  const handleCancelSelection = useCallback(() => {
    debouncedHandleSelectionChange.cancel();
    setSelectedRange(null);
    setSelectedStartIndex(null);
    setSelectedEndIndex(null);
    setCreateSegmentError(null);
    window.getSelection()?.removeAllRanges();
  }, [debouncedHandleSelectionChange]);

  const handleWordUpdate = useCallback(
    (wordIndex: number, newText: string) => {
      setEdits((prev) => {
        if (newText === transcriptItems[wordIndex]?.text) {
          const next = { ...prev };
          delete next[wordIndex];
          return next;
        }
        return { ...prev, [wordIndex]: newText };
      });
    },
    [transcriptItems],
  );

  const toggleSearch = useCallback(() => {
    setSearchExpanded((v) => !v);
    if (!searchExpanded) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearchQuery('');
    }
  }, [searchExpanded]);

  // const toggleEditMode = useCallback(() => {
  //   setIsEditMode((v) => !v);
  // }, []);

  /**
   * Find transcript indices that correspond to a time range.
   */
  const findIndicesFromRange = useCallback(
    (start: number, end: number): { startIndex: number; endIndex: number } => {
      if (!transcriptItems.length) return { startIndex: 0, endIndex: 0 };
      let startIndex = transcriptItems.findIndex((item, i) => {
        const nextStart = transcriptItems[i + 1]?.start;
        const itemEnd =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- nextStart is undefined when i+1 out of bounds
          nextStart !== undefined ? nextStart : item.end;
        return itemEnd > start;
      });
      if (startIndex === -1) startIndex = 0;
      const endIndex = transcriptItems.reduce(
        (best, item, i) => (item.start < end ? i : best),
        startIndex,
      );
      return {
        startIndex: Math.min(startIndex, endIndex),
        endIndex: Math.max(startIndex, endIndex),
      };
    },
    [transcriptItems],
  );

  const [editingStart, setEditingStart] = useState(false);
  const [editingEnd, setEditingEnd] = useState(false);
  const [startInputStr, setStartInputStr] = useState('');
  const [endInputStr, setEndInputStr] = useState('');

  const handleStartTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setStartInputStr(e.target.value);
  }, []);

  const handleEndTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndInputStr(e.target.value);
  }, []);

  const handleStartTimeBlur = useCallback(() => {
    setEditingStart(false);
    if (!selectedRange) return;
    const sec = parseTimeToSeconds(startInputStr);
    if (sec === null) return;
    const newStart = Math.max(0, Math.min(sec, selectedRange.end - 0.1));
    const newEnd = selectedRange.end;
    setSelectedRange({ start: newStart, end: newEnd });
    const { startIndex, endIndex } = findIndicesFromRange(newStart, newEnd);
    setSelectedStartIndex(startIndex);
    setSelectedEndIndex(endIndex);
  }, [selectedRange, startInputStr, findIndicesFromRange]);

  const handleEndTimeBlur = useCallback(() => {
    setEditingEnd(false);
    if (!selectedRange) return;
    const sec = parseTimeToSeconds(endInputStr);
    if (sec === null) return;
    const maxEnd = transcriptItems.length
      ? (() => {
          const last = transcriptItems[transcriptItems.length - 1];
          return last.end;
        })()
      : selectedRange.end + 60;
    const newStart = selectedRange.start;
    const newEnd = Math.max(selectedRange.start + 0.1, Math.min(sec, maxEnd));
    setSelectedRange({ start: newStart, end: newEnd });
    const { startIndex, endIndex } = findIndicesFromRange(newStart, newEnd);
    setSelectedStartIndex(startIndex);
    setSelectedEndIndex(endIndex);
  }, [selectedRange, endInputStr, findIndicesFromRange, transcriptItems]);

  const handleStartTimeFocus = useCallback(() => {
    if (selectedRange) {
      setStartInputStr(toHHMMSS(selectedRange.start));
      setEditingStart(true);
    }
  }, [selectedRange]);

  const handleEndTimeFocus = useCallback(() => {
    if (selectedRange) {
      setEndInputStr(toHHMMSS(selectedRange.end));
      setEditingEnd(true);
    }
  }, [selectedRange]);

  const handleStartTimeKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  }, []);

  const handleEndTimeKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  }, []);

  // Handle dragging the start handle
  const handleStartHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      debouncedHandleSelectionChange.cancel();
      window.getSelection()?.removeAllRanges();
      document.body.style.userSelect = 'none';
      setIsDraggingHandle('start');
      if (selectedStartIndex !== null) {
        dragStartRef.current = {
          startX: e.clientX,
          startTime: transcriptItems[selectedStartIndex].start,
        };
        dragCurrentIndexRef.current = selectedStartIndex;
      }
    },
    [selectedStartIndex, transcriptItems, debouncedHandleSelectionChange],
  );

  // Handle dragging the end handle
  const handleEndHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      debouncedHandleSelectionChange.cancel();
      window.getSelection()?.removeAllRanges();
      document.body.style.userSelect = 'none';
      setIsDraggingHandle('end');
      if (selectedEndIndex !== null) {
        dragStartRef.current = {
          startX: e.clientX,
          startTime:
            transcriptItems[selectedEndIndex].end || transcriptItems[selectedEndIndex].start + 5,
        };
        dragCurrentIndexRef.current = selectedEndIndex;
      }
    },
    [selectedEndIndex, transcriptItems, debouncedHandleSelectionChange],
  );

  // Handle mouse move for dragging — querySelectorAll only hits visible (rendered) DOM nodes,
  // which is correct: the user can only drag to words visible on screen.
  useEffect(() => {
    if (!isDraggingHandle || selectedStartIndex === null || selectedEndIndex === null) return;

    const handleMouseMove = (e: MouseEvent): void => {
      if (!dragStartRef.current) return;

      const container = listOuterRef.current;
      if (!container) return;

      const currentIdx = dragCurrentIndexRef.current;
      if (currentIdx === null) return;

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Find which visible word the mouse is currently over
      const allElements = container.querySelectorAll('[data-word-index]');
      let targetIndex: number | null = null;

      for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i] as HTMLElement;
        const rect = el.getBoundingClientRect();
        if (
          mouseX >= rect.left &&
          mouseX <= rect.right &&
          mouseY >= rect.top &&
          mouseY <= rect.bottom
        ) {
          const indexAttr = el.getAttribute('data-word-index');
          const idx = indexAttr ? Number(indexAttr) : NaN;
          if (!Number.isNaN(idx)) {
            targetIndex = idx;
            break;
          }
        }
      }

      if (targetIndex === null) return;

      let newIndex: number | null = null;

      if (isDraggingHandle === 'start') {
        if (targetIndex < currentIdx) {
          newIndex = Math.max(0, currentIdx - 1);
        } else if (targetIndex > currentIdx) {
          newIndex = Math.min(currentIdx + 1, selectedEndIndex);
        }
      } else {
        if (targetIndex < currentIdx) {
          newIndex = Math.max(currentIdx - 1, selectedStartIndex);
        } else if (targetIndex > currentIdx) {
          newIndex = Math.min(transcriptItems.length - 1, currentIdx + 1);
        }
      }

      if (newIndex !== null && newIndex !== currentIdx) {
        dragCurrentIndexRef.current = newIndex;
        const newItem = transcriptItems[newIndex];
        if (isDraggingHandle === 'start') {
          setSelectedStartIndex(newIndex);
          setSelectedRange((prev) => (prev ? { ...prev, start: newItem.start } : null));
        } else {
          setSelectedEndIndex(newIndex);
          setSelectedRange((prev) =>
            prev ? { ...prev, end: newItem.end || newItem.start + 5 } : null,
          );
        }
      }
    };

    const handleMouseUp = (): void => {
      setIsDraggingHandle(null);
      dragStartRef.current = null;
      dragCurrentIndexRef.current = null;
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingHandle, selectedStartIndex, selectedEndIndex, transcriptItems]);

  // Listen for selection changes (debounced)
  useEffect(() => {
    document.addEventListener('selectionchange', debouncedHandleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', debouncedHandleSelectionChange);
    };
  }, [debouncedHandleSelectionChange]);

  // Track the previous chunk index to avoid redundant scrolls during video playback.
  // Only scroll when the active chunk changes (far less frequent than per-word changes).
  const prevScrolledChunkRef = useRef(-1);

  // Timestamp of the last explicit seek — suppresses auto-scroll for a short
  // window so it can't overwrite the precise word-level scroll positioning.
  const seekActiveUntilRef = useRef(0);

  // Track the last processed seekTrigger id — declared here (before the
  // auto-scroll effect) so the auto-scroll can check for pending seeks.
  const lastSeekTriggerId = useRef(-1);

  // Scroll the exact word to the top of the transcript panel and pulse-highlight it.
  // Uses a requestAnimationFrame loop plus a MutationObserver to continuously
  // hold the word near the top, defeating layout shifts from react-window's
  // resetAfterIndex / ResizeObserver re-measurements that can fire later than
  // the rAF window. The word is snapped with an 8px margin from the top so a
  // small late shift still leaves the word inside the visible viewport.
  const seekRafRef = useRef(0);
  const seekClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekMutationObserverRef = useRef<MutationObserver | null>(null);
  const seekAbortCleanupRef = useRef<(() => void) | null>(null);

  const applySeekHighlight = useCallback(
    (startTime: number | null) => {
      // Cancel any in-flight seek animation and observers
      cancelAnimationFrame(seekRafRef.current);
      if (seekClearTimer.current) clearTimeout(seekClearTimer.current);
      if (seekMutationObserverRef.current) {
        seekMutationObserverRef.current.disconnect();
        seekMutationObserverRef.current = null;
      }
      if (seekAbortCleanupRef.current) {
        seekAbortCleanupRef.current();
        seekAbortCleanupRef.current = null;
      }
      if (startTime === null) return;
      const container = listOuterRef.current;
      if (!container) return;

      // Snap with a small margin from the top so that a late layout shift of
      // a few pixels does not push the target word above the visible viewport.
      const SNAP_OFFSET_PX = 8;
      // Hold the snap for long enough to catch ResizeObserver re-measurements
      // that fire after react-window mounts the newly-visible chunk(s).
      const HOLD_MS = 2500;
      // Suppress auto-scroll for the full hold window plus the highlight tail
      // so the playback-driven scroll cannot fight with our snap.
      seekActiveUntilRef.current = Date.now() + HOLD_MS + 500;

      const started = Date.now();
      let highlightApplied = false;
      let foundOnce = false;
      let aborted = false;

      const snapToWord = (): boolean => {
        const el = container.querySelector<HTMLElement>(`[data-start-time="${startTime}"]`);
        if (!el) return false;
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta = elRect.top - containerRect.top - SNAP_OFFSET_PX;
        if (Math.abs(delta) > 1) container.scrollTop += delta;
        return true;
      };

      // If the user actively scrolls or presses a scroll key, stop fighting
      // them — keep the highlight, drop the auto-snap.
      const abort = (): void => {
        aborted = true;
        cancelAnimationFrame(seekRafRef.current);
        if (seekMutationObserverRef.current) {
          seekMutationObserverRef.current.disconnect();
          seekMutationObserverRef.current = null;
        }
        if (seekAbortCleanupRef.current) {
          seekAbortCleanupRef.current();
          seekAbortCleanupRef.current = null;
        }
      };
      const onWheel = (): void => abort();
      const onTouchStart = (): void => abort();
      const onKeyDown = (e: KeyboardEvent): void => {
        if (
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'PageUp' ||
          e.key === 'PageDown' ||
          e.key === 'Home' ||
          e.key === 'End' ||
          e.key === ' '
        ) {
          abort();
        }
      };
      container.addEventListener('wheel', onWheel, { passive: true });
      container.addEventListener('touchstart', onTouchStart, { passive: true });
      container.addEventListener('keydown', onKeyDown);
      seekAbortCleanupRef.current = (): void => {
        container.removeEventListener('wheel', onWheel);
        container.removeEventListener('touchstart', onTouchStart);
        container.removeEventListener('keydown', onKeyDown);
      };

      // MutationObserver catches layout shifts that happen between rAF ticks
      // (react-window mounts new chunks, ResizeObserver fires resetAfterIndex,
      // styles update) and re-snaps the word immediately.
      const mo = new MutationObserver(() => {
        if (aborted) return;
        snapToWord();
      });
      mo.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      seekMutationObserverRef.current = mo;

      const loop = (): void => {
        if (aborted) return;
        const elapsed = Date.now() - started;

        if (snapToWord()) {
          foundOnce = true;

          if (!highlightApplied && elapsed >= 150) {
            highlightApplied = true;
            setSeekHighlightStart(startTime);
          }
        }

        // If the target chunk never renders within 1500ms, give up.
        const maxMs = foundOnce ? HOLD_MS : 1500;
        if (elapsed < maxMs) {
          seekRafRef.current = requestAnimationFrame(loop);
        } else {
          // Disconnect the observer and listeners, then fade out the highlight.
          if (seekMutationObserverRef.current) {
            seekMutationObserverRef.current.disconnect();
            seekMutationObserverRef.current = null;
          }
          if (seekAbortCleanupRef.current) {
            seekAbortCleanupRef.current();
            seekAbortCleanupRef.current = null;
          }
          seekClearTimer.current = setTimeout(() => setSeekHighlightStart(null), 1500);
        }
      };

      seekRafRef.current = requestAnimationFrame(loop);
    },
    [setSeekHighlightStart],
  );

  // Clean up any in-flight seek loop on unmount.
  useEffect(() => {
    return () => {
      cancelAnimationFrame(seekRafRef.current);
      if (seekClearTimer.current) clearTimeout(seekClearTimer.current);
      if (seekMutationObserverRef.current) seekMutationObserverRef.current.disconnect();
      if (seekAbortCleanupRef.current) seekAbortCleanupRef.current();
    };
  }, []);

  // ── Auto-scroll during playback ──────────────────────────────────────────
  // Keeps the transcript in sync with video playback (small time increments).
  // Suppressed for a short window after an explicit seek so it can't overwrite
  // the precise word-level scroll that applySeekHighlight set.
  useEffect(() => {
    if (currentChunkIndex === -1) return;
    if (currentPosition?.source === 'waveform-segment-resize') return;

    // Suppress when an explicit segment/clip/topic time-click just fired.
    // The seek-trigger effect (declared below) handles scrolling for these;
    // auto-scroll must not compete or it can overwrite the word-level snap.
    if (currentPosition?.source === 'segment-click') return;

    // Suppress while an explicit seek is active
    if (Date.now() < seekActiveUntilRef.current) return;

    // Skip when a fresh seekTrigger is pending — the seek-trigger effect
    // below will handle precise scroll + highlight for this render.
    if (seekTrigger && seekTrigger.id !== lastSeekTriggerId.current) return;

    // Skip if chunk hasn't changed (avoid redundant scrolls during playback)
    if (currentChunkIndex === prevScrolledChunkRef.current) return;
    prevScrolledChunkRef.current = currentChunkIndex;

    if (disableVirtualization) {
      const activeIndex = transcriptItems.findIndex((item) => item.start === activeWordStart);
      if (activeIndex !== -1) {
        const container = listOuterRef.current;
        const target = container?.querySelector<HTMLElement>(`[data-word-index="${activeIndex}"]`);
        target?.scrollIntoView({ block: 'start', inline: 'nearest' });
      }
      return;
    }

    listRef.current?.scrollToItem(currentChunkIndex, 'start');
  }, [
    currentChunkIndex,
    currentPosition?.source,
    seekTrigger,
    disableVirtualization,
    transcriptItems,
    activeWordStart,
  ]);

  // ── Explicit seek (segment/clip/topic time click) ──────────────────────
  // Driven by the seekTrigger prop — completely independent of the
  // currentPosition source field, so it can't be overwritten by the
  // video's timeupdate batching the state to 'video-playing'.
  useEffect(() => {
    if (!seekTrigger || seekTrigger.id === lastSeekTriggerId.current) return;
    lastSeekTriggerId.current = seekTrigger.id;

    // Immediately suppress auto-scroll so any subsequent render (e.g. from
    // video timeupdate) can't overwrite the scroll position we're about to set.
    seekActiveUntilRef.current = Date.now() + 1200;

    const timeSec = seekTrigger.timeSec;
    const items = transcriptItems;

    // Use the same word-finding algorithm as currentTranscriptionItem
    const word = findWordAtTime(items, timeSec);
    if (!word) return;

    const wordStart = word.start;
    const wordIndex = items.findIndex((w) => w.start === wordStart);
    if (wordIndex === -1) return;
    const phraseIndex = phrases.findIndex(
      (p) => wordIndex >= p.startIndex && wordIndex <= p.endIndex,
    );
    if (phraseIndex === -1) return;
    const chunkIndex = Math.floor(phraseIndex / PHRASES_PER_CHUNK);

    prevScrolledChunkRef.current = chunkIndex;

    if (disableVirtualization) {
      const container = listOuterRef.current;
      const target = container?.querySelector<HTMLElement>(`[data-word-index="${wordIndex}"]`);
      target?.scrollIntoView({ block: 'start', inline: 'nearest' });
      applySeekHighlight(wordStart);
      return;
    }

    listRef.current?.scrollToItem(chunkIndex, 'start');
    applySeekHighlight(wordStart);
  }, [seekTrigger, transcriptItems, phrases, disableVirtualization, applySeekHighlight]);

  // Stable itemData object — only recreated when dependencies change.
  // Passed as the `itemData` prop to VariableSizeList and forwarded to each PhraseRow.
  const itemData = useMemo<PhraseRowData>(
    () => ({
      paragraphChunks,
      edits,
      selectedStartIndex,
      selectedEndIndex,
      currentChunkIndex,
      activeWordStart,
      seekHighlightStart,
      searchQuery,
      searchMatchIndices,
      searchMatchCount,
      currentMatchIndex,
      isEditMode,
      handleStartHandleMouseDown,
      handleEndHandleMouseDown,
      handleWordUpdate,
      selectItem,
      setRowHeight,
    }),
    [
      paragraphChunks,
      edits,
      selectedStartIndex,
      selectedEndIndex,
      currentChunkIndex,
      activeWordStart,
      seekHighlightStart,
      searchQuery,
      searchMatchIndices,
      searchMatchCount,
      currentMatchIndex,
      isEditMode,
      handleStartHandleMouseDown,
      handleEndHandleMouseDown,
      handleWordUpdate,
      selectItem,
      setRowHeight,
    ],
  );

  return (
    <div className={styles.transcriptWrapper}>
      {/* Header */}
      <div className={styles.transcriptHeader}>
        <span className={styles.transcriptTitle}>TRANSCRIPT</span>
        <div className={styles.transcriptActions}>
          <button
            type="button"
            className={`${styles.actionButton} ${searchExpanded ? styles.actionButtonActive : ''}`}
            title="Search in transcript"
            onClick={toggleSearch}
            aria-expanded={searchExpanded}
          >
            <img src={searchIcon} alt="Search" />
          </button>
          <button
            type="button"
            className={styles.actionButton}
            title="Open in popup"
            onClick={onPopout}
          >
            <img src={fullScreenIcon} alt="Open in popup" />
          </button>
        </div>
      </div>

      {/* Search bar - visible when search is expanded */}
      {searchExpanded && (
        <div className={styles.searchBar}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search in transcript..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search in transcript"
          />
          <span className={styles.searchResults}>
            {searchQuery.trim()
              ? `${searchMatchCount > 0 ? currentMatchIndex + 1 : 0} of ${searchMatchCount}`
              : '—'}
          </span>
          <button
            type="button"
            className={styles.searchNavButton}
            title="Previous match"
            disabled={!canGoPrev}
            onClick={goToPrevMatch}
            aria-label="Previous match"
          >
            ↑
          </button>
          <button
            type="button"
            className={styles.searchNavButton}
            title="Next match"
            disabled={!canGoNext}
            onClick={goToNextMatch}
            aria-label="Next match"
          >
            ↓
          </button>
          <button
            type="button"
            className={styles.searchCloseButton}
            title="Close search"
            onClick={() => setSearchExpanded(false)}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
      )}

      {/* Transcript Content */}
      <div className={styles.transcriptContent}>
        {/* Container div measured by ResizeObserver to provide pixel dimensions to VariableSizeList */}
        <div
          ref={disableVirtualization ? listOuterRef : containerRef}
          className={styles.transcriptContentScroll}
          style={disableVirtualization ? undefined : { overflow: 'hidden' }}
        >
          {disableVirtualization ? (
            <div style={{ paddingLeft: 16, paddingRight: 16, boxSizing: 'border-box' }}>
              {paragraphChunks.map((chunk, chunkIndex) => {
                const isCurrentChunk = chunkIndex === currentChunkIndex;
                return (
                  <span key={chunkIndex} className={styles.phraseRow}>
                    {chunk.map((phrase) => (
                      <span key={phrase.startIndex} className={styles.phraseRow}>
                        {phrase.words.map((item, wordIdx) => {
                          const globalIndex = phrase.startIndex + wordIdx;
                          const displayText = edits[globalIndex] ?? item.text;
                          const displayItem = { ...item, text: displayText };
                          const isSelected =
                            selectedStartIndex !== null &&
                            selectedEndIndex !== null &&
                            globalIndex >= selectedStartIndex &&
                            globalIndex <= selectedEndIndex;
                          const isCurrent = isCurrentChunk && activeWordStart === item.start;
                          const isStartHandle = selectedStartIndex === globalIndex;
                          const isEndHandle = selectedEndIndex === globalIndex;
                          const isFirstSelected = isSelected && globalIndex === selectedStartIndex;
                          const isLastSelected = isSelected && globalIndex === selectedEndIndex;
                          const isSearchMatch = !!(
                            searchQuery.trim() && searchMatchIndices.includes(globalIndex)
                          );
                          const isCurrentSearchMatch =
                            searchMatchCount > 0 &&
                            searchMatchIndices[currentMatchIndex] === globalIndex;

                          const isSeekHighlight = seekHighlightStart === item.start;
                          const wrapperClass = [
                            styles.wordWrapper,
                            isSelected ? styles.selectedWord : '',
                            isFirstSelected ? styles.selectedWordFirst : '',
                            isLastSelected ? styles.selectedWordLast : '',
                            isSearchMatch ? styles.searchMatch : '',
                            isCurrentSearchMatch ? styles.searchMatchCurrent : '',
                            isSeekHighlight ? styles.seekHighlight : '',
                          ]
                            .filter(Boolean)
                            .join(' ');

                          return (
                            <span
                              key={item.start}
                              className={wrapperClass}
                              data-start-time={item.start}
                              data-end-time={item.end}
                              data-word-index={globalIndex}
                            >
                              {isStartHandle && (
                                <span
                                  className={`${styles.selectionHandle} ${styles.startHandle}`}
                                  onMouseDown={handleStartHandleMouseDown}
                                  title="Drag to adjust start time"
                                >
                                  <span className={styles.handleGrip} />
                                </span>
                              )}
                              <TranscriptItem
                                item={displayItem}
                                selected={isCurrent}
                                editable={isEditMode}
                                onSelect={selectItem}
                                onUpdate={
                                  isEditMode
                                    ? (newText: string) => handleWordUpdate(globalIndex, newText)
                                    : undefined
                                }
                                className={isSelected ? styles.selectedWordText : undefined}
                              />
                              {isEndHandle && (
                                <span
                                  className={`${styles.selectionHandle} ${styles.endHandle}`}
                                  onMouseDown={handleEndHandleMouseDown}
                                  title="Drag to adjust end time"
                                >
                                  <span className={styles.handleGrip} />
                                </span>
                              )}
                              {wordIdx < phrase.words.length - 1 && '\u00A0'}
                            </span>
                          );
                        })}
                      </span>
                    ))}
                  </span>
                );
              })}
            </div>
          ) : (
            <VariableSizeList
              ref={listRef}
              outerRef={listOuterRef}
              // In popout windows the measured height can temporarily be 0.
              // Fall back to an estimated height so the list mounts and then
              // ResizeObserver can correct it.
              height={Math.max(listDimensions.height, ESTIMATED_ROW_HEIGHT)}
              width={effectiveListWidth}
              itemCount={paragraphChunks.length}
              itemSize={getItemSize}
              estimatedItemSize={ESTIMATED_ROW_HEIGHT}
              itemData={itemData}
              overscanCount={3}
              style={{ overflowX: 'hidden' }}
            >
              {PhraseRow}
            </VariableSizeList>
          )}
        </div>

        {/* Bottom Bar — only visible when a range is selected */}
        {selectedRange && (
          <div className={styles.selectionBar}>
            {/* Cancel */}
            <button className={styles.cancelButton} onClick={handleCancelSelection}>
              Cancel
            </button>

            {/* START → FINISH time inputs */}
            <div className={styles.timeInputs}>
              <div className={styles.timeInput}>
                <span className={styles.timeLabel}>START</span>
                <input
                  type="text"
                  className={styles.timeValueInput}
                  value={editingStart ? startInputStr : toHHMMSS(selectedRange.start)}
                  onChange={handleStartTimeChange}
                  onFocus={handleStartTimeFocus}
                  onBlur={handleStartTimeBlur}
                  onKeyDown={handleStartTimeKeyDown}
                  placeholder="00:00:00"
                  aria-label="Clip start time"
                />
              </div>

              {/* Long arrow separator */}
              <span className={styles.timeSeparator}>
                <img src={arrowIcon} alt="" aria-hidden="true" />
              </span>

              <div className={styles.timeInput}>
                <span className={styles.timeLabel}>FINISH</span>
                <input
                  type="text"
                  className={styles.timeValueInput}
                  value={editingEnd ? endInputStr : toHHMMSS(selectedRange.end)}
                  onChange={handleEndTimeChange}
                  onFocus={handleEndTimeFocus}
                  onBlur={handleEndTimeBlur}
                  onKeyDown={handleEndTimeKeyDown}
                  placeholder="00:00:00"
                  aria-label="Clip finish time"
                />
              </div>
            </div>

            {onCreateSegmentFromSelection && (
              <button
                type="button"
                className={styles.newClipButton}
                onClick={handleCreateSegment}
                disabled={isCreatingSegment}
                aria-busy={isCreatingSegment}
              >
                <img src={newClipIcon} alt="" aria-hidden="true" />
                <span>{isCreatingSegment ? 'Creating…' : ` ${createSelectionButtonLabel}`}</span>
              </button>
            )}
            {createSegmentError && (
              <span className={styles.selectionBarError} role="alert">
                {createSegmentError}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkbenchTranscript;
